import type { PendingAuthAction } from "@storytell/core";
import type { RichText } from "@storytell/cp-client";
import { dispatchGlobalEvent } from "@storytell/events";
import { richTextToProseMirror } from "@storytell/rich-text/src/converters/richTextToProseMirror";
import { isServer } from "solid-js/web";

const STORAGE_KEY = "storytell:pending-auth-action";
const EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

interface StoredPendingAction {
  action: PendingAuthAction;
  timestamp: number;
}

/**
 * Save a pending action to sessionStorage before auth redirect.
 * The action will be retrieved and executed after successful auth.
 */
export const savePendingAuthAction = (action: PendingAuthAction): void => {
  if (isServer) return;

  const stored: StoredPendingAction = {
    action,
    timestamp: Date.now(),
  };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch (error) {
    console.error("[PendingAuthAction] Failed to save pending action:", error);
  }
};

/**
 * Retrieve and clear the pending action from sessionStorage.
 * Returns null if no action exists or if the action has expired.
 */
export const getPendingAuthAction = (): StoredPendingAction | null => {
  if (isServer) return null;

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as StoredPendingAction;

    // Check expiry
    if (Date.now() - parsed.timestamp > EXPIRY_MS) {
      clearPendingAuthAction();
      return null;
    }

    return parsed;
  } catch (error) {
    console.error("[PendingAuthAction] Failed to get pending action:", error);
    return null;
  }
};

/**
 * Clear the pending action from sessionStorage.
 */
export const clearPendingAuthAction = (): void => {
  if (isServer) return;

  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("[PendingAuthAction] Failed to clear pending action:", error);
  }
};

/** A captured prompt is the prompt bar's rich-text document; anything else, such as a plain-text capture from an earlier build, is dropped. */
const isCapturedPrompt = (data: unknown): data is RichText.RichText =>
  typeof data === "object" && data !== null && Array.isArray((data as { content?: unknown }).content);

/**
 * Execute the pending action based on its type.
 * Dispatches appropriate global events to restore state.
 */
const executePendingAuthAction = (stored: StoredPendingAction): void => {
  const { action } = stored;

  switch (action.type) {
    case "prompt": {
      // The capture is the submission's rich text: a filled variable comes back as the text
      // typed into it, pasted chips and mention pills as themselves. Only fills the bar; the
      // user sends it.
      if (isCapturedPrompt(action.data)) {
        dispatchGlobalEvent("set-prompt-bar-doc", { doc: richTextToProseMirror(action.data) });
      }
      break;
    }
    case "upload": {
      // Dispatch event to open file picker
      dispatchGlobalEvent("open-file-picker", { openKnowledgePane: false });
      break;
    }
    case "project":
    case "integration":
    case "share": {
      // These actions typically don't need post-auth restoration
      // as the user will naturally re-attempt the action
      break;
    }
  }

  clearPendingAuthAction();
};

/**
 * Check if there's a pending action and execute it.
 * Should be called after successful authentication.
 */
export const checkAndExecutePendingAction = (): void => {
  const stored = getPendingAuthAction();
  if (stored) {
    executePendingAuthAction(stored);
  }
};
